import { useContext, useMemo } from 'react';
import { CurrenciesContext } from '../../contexts/CurrenciesContext';
import { SecuritiesContext } from '../../contexts/SecuritiesContext';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import {
  EMPTY_RELATED_ENTITIES_LOOKUP_MAPS,
  resolveRelatedEntities,
  type RelatedEntitiesConfig,
  type RelatedEntitiesLookupMaps,
} from './relatedEntities';
import type { JsonModalDocument } from './types';

export function useRelatedEntitiesLookupMaps(): RelatedEntitiesLookupMaps {
  // Intentionally useContext, not useCurrenciesContext / useSecuritiesContext: those hooks throw when
  // providers are missing. BlotterTable menus run on every table (including kyoko tests and blotters
  // outside Currencies/Securities providers); empty maps here mean lookup misses, not a crash.
  const currenciesContext = useContext(CurrenciesContext);
  const securitiesContext = useContext(SecuritiesContext);

  return useMemo(
    () => ({
      currenciesBySymbol:
        currenciesContext?.currenciesBySymbol ?? EMPTY_RELATED_ENTITIES_LOOKUP_MAPS.currenciesBySymbol,
      securitiesBySymbol:
        securitiesContext?.securitiesBySymbol ?? EMPTY_RELATED_ENTITIES_LOOKUP_MAPS.securitiesBySymbol,
    }),
    [currenciesContext?.currenciesBySymbol, securitiesContext?.securitiesBySymbol]
  );
}

export function useResolveRelatedEntitiesDocuments<R>(
  config?: RelatedEntitiesConfig<R>
): (data: R) => JsonModalDocument[] {
  const maps = useRelatedEntitiesLookupMaps();

  return useCallbackRef((data: R) => (config != null ? resolveRelatedEntities(data, config, maps) : []));
}
